import axios, { AxiosResponse } from 'axios';
import {
  ApiResponse,
  QuestionsData,
  FormConfigurationData,
  VisibilityAnalyzeRequest,
  VisibilityAnalysisResult,
  AuthVerificationRequest,
  AuthVerifyCodeRequest,
  AuthResponse,
  ApiError,
  QueriesData
} from '../types/api';

// Configure base URL - you can change this to your backend URL
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log('API Response interceptor - Success:', response);
    return response;
  },
  (error) => {
    console.error('API Response interceptor - Error:', error);
    console.log('Error response:', error.response);
    console.log('Error response data:', error.response?.data);
    console.log('Error response status:', error.response?.status);
    
    const apiError: ApiError = {
      message: error.response?.data?.message || error.message || 'An unexpected error occurred',
      status: error.response?.status || 500,
    };
    console.log('Formatted API Error:', apiError);
    return Promise.reject(apiError);
  }
);

// API Service Functions
export const apiService = {
  // Health Check
  healthCheck: async (): Promise<any> => {
    const response: AxiosResponse<any> = await api.get('/health');
    return response.data;
  },

  // Questions API (FAQ questions)
  getQuestions: async (): Promise<QuestionsData> => {
    const response: AxiosResponse<ApiResponse<QuestionsData>> = await api.get('/api/v1/questions');
    // Handle both API Gateway format (response.data.data) and Lambda URL format (response.data)
    return response.data.data || response.data;
  },

  // Form Configuration API
  getFormConfiguration: async (formName?: string): Promise<FormConfigurationData> => {
    const params = formName ? { name: formName } : {};
    const response: AxiosResponse<ApiResponse<FormConfigurationData>> = await api.get('/api/v1/forms', { params });
    // Handle both API Gateway format (response.data.data) and Lambda URL format (response.data)
    return response.data.data || response.data;
  },

  // Queries API
  getQueries: async (aiPlatform?: string, status?: boolean): Promise<QueriesData> => {
    const params: Record<string, any> = {};
    if (aiPlatform) params.ai_platform = aiPlatform;
    if (status !== undefined) params.status = status;
    
    const response: AxiosResponse<ApiResponse<QueriesData>> = await api.get('/api/v1/queries', { params });
    // Handle both API Gateway format (response.data.data) and Lambda URL format (response.data)
    return response.data.data || response.data;
  },

  // Visibility Analysis API
  analyzeVisibility: async (request: VisibilityAnalyzeRequest): Promise<VisibilityAnalysisResult> => {
    // AI analysis can take longer, so we increase timeout to 5 minutes
    const response: AxiosResponse<ApiResponse<VisibilityAnalysisResult>> = await api.post(
      '/api/v1/visibility/analyze',
      request,
      { timeout: 300000 } // 5 minutes timeout for AI analysis
    );
    // Handle both API Gateway format (response.data.data) and Lambda URL format (response.data)
    return response.data.data || response.data;
  },

  calculateVisibilityScore: async (analyticsData: any): Promise<any> => {
    const response: AxiosResponse<ApiResponse<any>> = await api.post(
      '/api/v1/visibility/score',
      analyticsData
    );
    // Handle both API Gateway format (response.data.data) and Lambda URL format (response.data)
    return response.data.data || response.data;
  },

  // Authentication API
  sendVerificationCode: async (request: AuthVerificationRequest): Promise<AuthResponse> => {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await api.post(
      '/api/v1/auth/send-verification',
      request
    );
    // Handle both API Gateway format (response.data.data) and Lambda URL format (response.data)
    return response.data.data || response.data;
  },

  verifyCode: async (request: AuthVerifyCodeRequest): Promise<AuthResponse> => {
    console.log('=== API SERVICE DEBUG ===');
    console.log('Base URL:', BASE_URL);
    console.log('Request:', request);
    console.log('Full URL:', `${BASE_URL}/api/v1/auth/verify-code`);
    
    try {
      const response: AxiosResponse<ApiResponse<AuthResponse>> = await api.post(
        '/api/v1/auth/verify-code',
        request
      );
      
      console.log('=== RAW API RESPONSE ===');
      console.log('Status:', response.status);
      console.log('Headers:', response.headers);
      console.log('Raw data:', response.data);
      console.log('Response structure check:');
      console.log('- response.data exists:', !!response.data);
      console.log('- response.data.data exists:', !!response.data?.data);
      console.log('- response.data.success:', response.data?.success);
      console.log('- response.data.data.verified:', response.data?.data?.verified);
      
      // Handle both API Gateway format (response.data.data) and Lambda URL format (response.data)
      const responseData = response.data.data || response.data;
      
      if (!responseData) {
        console.error('❌ Invalid response structure');
        throw new Error('Invalid response structure from server');
      }
      
      return responseData;
    } catch (error) {
      console.error('=== API SERVICE ERROR ===');
      console.error('Error:', error);
      throw error;
    }
  },

  getSessionStatus: async (email: string): Promise<AuthResponse> => {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await api.get(
      `/api/v1/auth/session-status?email=${encodeURIComponent(email)}`
    );
    // Handle both API Gateway format (response.data.data) and Lambda URL format (response.data)
    return response.data.data || response.data;
  },

  logout: async (email: string): Promise<AuthResponse> => {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await api.post(
      '/api/v1/auth/logout',
      { email }
    );
    // Handle both API Gateway format (response.data.data) and Lambda URL format (response.data)
    return response.data.data || response.data;
  },

  async saveUserResponses(data: {
    business_info: {
      name: string;
      domain: string;
      industry: string;
      city: string;
      state: string;
      country: string;
    };
    responses: Record<string, any>;
    user_email: string;
  }): Promise<any> {
    console.log('Saving user responses:', data);
    const response = await fetch(`${BASE_URL}/api/v1/responses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-API-Key': process.env.REACT_APP_AUTOMATION_API_KEY || '',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },
};

export default apiService;
